/**
 * 福井県 名物めし 12段階（コンテンツデータ）。
 *
 * 数値（radius / mass / score / unlockScore）は福岡・北海道・山形と同じテンプレート。
 * 進行の軸は「物理的な大きさ ＝ ごちそう度」で、県ごとに触らない。
 *
 * 調査で出てきた「書いてはいけないこと」を反映している。
 *  - ソースカツ丼を「福井発祥」と断定しない。初披露の地は東京。
 *  - ボルガライスの名前の由来・発祥店は書かない。越前市公式が「分かっていない」と明記。
 *  - へしこ・でっち羊かんの語源は諸説。断定しない。
 *  - 「越前がに＝越前町産」と書かない。福井県の港で水揚げされたズワイガニの総称。
 *  - 油揚げ「日本一」は、農水省の記載では家計調査の都市ランキング（福井市）が母集団。
 *  - 店名（谷口屋・ヨーロッパ軒・錦梅堂）は表示名に使わない。
 *  - 福井梅の県公式表記は「紅サシ」。
 *
 * 本番画像は public/assets/foods/fukui/ に置く。
 *
 * 2026-10-02 確定案（audit/_確定案.csv）で料理を入れ替えた。
 * - 外した品：福井梅・おぼろ昆布・小鯛のささ漬・でっち羊かん（店で食べられる品に差し替え）
 * - 純けい焼き（親鶏の焼き鳥）：特定のチェーン店名（秋吉など）は店の宣伝になるので書かない
 * - 純けい焼き（親鶏の焼き鳥）：「福井は焼き鳥の消費量が全国トップレベル」は福いろに記載があるが、順位の主張で全国統計の出典を確かめていないので書かない
 * - 純けい焼き（親鶏の焼き鳥）：発祥の店・発祥の年は公式で確かめられないので書かない
 * - 敦賀ラーメン：営業時間（午後8時〜深夜2時ごろ）は変わる情報なので書かない
 * - 敦賀ラーメン：「豚骨鶏ガラに醤油を加えた濁ったスープ」は観光協会ページ内の特定の屋台1軒の紹介文なので、敦賀ラーメン全般の説明として使わない
 * - 敦賀ラーメン：個々の屋台・店の名前と創業年は店の宣伝になるので書かない
 * - 浜焼き鯖：鯖街道で京都へ運ばれた話は農水省ページにもあるが、description と tip の字数に収めるため今回は入れていない（書いてよい事実）
 * - 浜焼き鯖：販売店・飲食店の名前は書かない
 * - 若狭ぐじ（甘鯛）の料理：北大路魯山人の評（ふくいドットコムに記載）は人物の発言の孫引きになるので書かない
 * - 若狭ぐじ（甘鯛）の料理：「若狭ぐじ極」（800g以上・活〆）は福井県ページにあるが、品の説明から外れるので書かない
 */

import { attachHulls } from '../../utils/itemShape';
import type { RewardLink, StageDefinition, StageItemDefinition } from '../types';

import { FUKUI_FOOD_HULLS } from './fukuiFoodShapes.generated';
import { fukuiSceneryTour } from './fukuiScenes';

/** 「食べに行く」導線の既定リンク（特定店の宣伝にならないよう検索リンクにする）。 */
function searchLinks(area: string, dish: string): RewardLink[] {
  return [
    {
      label: '地図で探す',
      url: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${area} ${dish}`)}`,
    },
    {
      label: 'お店を調べる',
      url: `https://www.google.com/search?q=${encodeURIComponent(`${area} ${dish}`)}`,
    },
  ];
}

const FOOD_ITEMS: StageItemDefinition[] = [
  {
    id: 'habutae-mochi',
    level: 1,
    name: '羽二重餅',
    shortName: '羽二重餅',
    image: '/assets/foods/fukui/01_habutae_mochi.webp',
    radius: 17,
    mass: 1,
    score: 2,
    description: '絹織物の名を持つ、やわらかい餅。',
    source: 'https://fuku-iro.jp/feature/detail_200.html',
    icon: { label: '羽二重', shape: 'mochi', baseColor: '#f4efe4', accentColor: '#cbb98f' },
    reward: {
      unlockScore: 200,
      area: '福井市',
      headline: '名前は福井の絹織物「羽二重織」から。真珠のような光沢と絹のような手ざわりを餅に見立てた。',
      tip: '原料は餅粉・砂糖・水あめの三つだけ。いちばん古い作り手は弘化四年（1847年）の創業とされる。',
      links: searchLinks('福井市', '羽二重餅'),
    },
  },
  {
    id: 'junkei-yakitori',
    level: 2,
    name: '純けい焼き（親鶏の焼き鳥）',
    shortName: '純けい焼き',
    image: '/assets/foods/fukui/02_junkei_yakitori.webp',
    radius: 21,
    mass: 1.3,
    score: 4,
    description: '卵を産んだ後の親鳥（メス鶏）のもも肉と皮だけを使って焼き上げる焼き鳥。若鳥より肉が固く脂肪分は少ないが、歯ごたえがあり、噛むほどに旨味があふれ出す。',
    source:
      'https://fuku-iro.jp/spot-category/yakitori/',
    icon: { label: '純けい', shape: 'dumpling', baseColor: '#b9783a', accentColor: '#5e3a1c' },
    reward: {
      unlockScore: 500,
      area: '福井市ほか県内各地',
      headline: '親鳥の歯ごたえを味わう焼き鳥',
      tip: '居酒屋だけでなく、持ち帰りの焼き鳥が晩ご飯のおかずにも並ぶ。盆や正月の食卓にもよく出て、スーパーの総菜売り場にも塩とタレが定番で置かれる。',
      links: searchLinks('福井市', '純けい 焼き鳥'),
      official: [{ label: '福井市公式観光サイト 福いろ「じゅんけいとは？」', url: 'https://fuku-iro.jp/spot-category/yakitori/' }, { label: '越前おおの観光ガイド「純けい」', url: 'https://www.ono-kankou.jp/gourmet/%E7%B4%94%E3%81%91%E3%81%84/' }],
    },
  },
  {
    id: 'heshiko',
    level: 3,
    name: 'へしこ',
    shortName: 'へしこ',
    image: '/assets/foods/fukui/03_heshiko.webp',
    radius: 26,
    mass: 1.7,
    score: 8,
    description: '糠の床で、半年寝かせる。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/heshiko_fukui.html',
    icon: { label: 'へしこ', shape: 'fish', baseColor: '#6b4a32', accentColor: '#d8c49c' },
    reward: {
      unlockScore: 1000,
      area: '若狭・越前海岸',
      headline: 'サバを背開きにして塩漬けし、こぬかと塩の床に重石をして、冷暗所で半年ほど寝かせる保存食。',
      tip: '江戸時代の中ごろには作られていた。語源は「圧しこむ」説、塩漬けの水分「ひしお」がなまった説など諸説。炙ってそのまま食べたり、お茶漬けやおにぎりの具にする。',
      links: searchLinks('福井県', 'へしこ'),
    },
  },
  {
    id: 'takeda-aburaage',
    level: 4,
    name: '竹田の油あげ',
    shortName: '油あげ',
    image: '/assets/foods/fukui/04_takeda_aburaage.webp',
    radius: 30,
    mass: 2.2,
    score: 16,
    description: '外はぱりっと、中はふんわり。',
    source: 'https://kanko-sakai.com/gourmets/g018/',
    icon: { label: '油あげ', shape: 'mochi', baseColor: '#d9a256', accentColor: '#8c5a24' },
    reward: {
      unlockScore: 1800,
      area: '坂井市丸岡町',
      headline: '福井は油揚げをよく食べる土地。農林水産省は、油揚げの消費額が50年以上1位（家計調査・二人以上の世帯・都道府県庁所在市などのランキング、2017〜2019年平均）と記している。',
      tip: '嶺北では浄土真宗の報恩講料理に厚揚げが欠かせない。竹田の油あげは大正14年から100年近く受け継がれた作り方で揚げられる。',
      links: searchLinks('坂井市', '竹田の油あげ'),
    },
  },
  {
    id: 'tsuruga-ramen',
    level: 5,
    name: '敦賀ラーメン',
    shortName: '敦賀ラーメン',
    image: '/assets/foods/fukui/05_tsuruga_ramen.webp',
    radius: 36,
    mass: 2.8,
    score: 32,
    description: 'トンコツと鶏ガラのスープが基本のラーメン。敦賀駅前の流しの屋台から始まり、トラック輸送が盛んになると国道8号沿いに十数軒の屋台が並んだ。50年以上たったいまも屋台や店舗で食べられている。',
    source:
      'https://tsuruga-kanko.jp/gourmet_souvenir/tsuruga-ramen/',
    icon: { label: 'ラーメン', shape: 'ramen', baseColor: '#c9a06a', accentColor: '#5b8a3a' },
    reward: {
      unlockScore: 3000,
      area: '敦賀市',
      headline: '屋台から始まった港町の一杯',
      tip: '敦賀駅から氣比神宮交差点へ続く国道8号は「敦賀屋台ラーメン街道」とも呼ばれる。列車職員やトラック運転手、観光客の腹を満たしてきた。',
      links: searchLinks('敦賀市', '敦賀ラーメン'),
      official: [{ label: '敦賀観光公式サイト「敦賀のラーメン案内所」', url: 'https://tsuruga-kanko.jp/special/sp_ramen/' }],
    },
  },
  {
    id: 'oroshi-soba',
    level: 6,
    name: 'おろしそば',
    shortName: 'おろしそば',
    image: '/assets/foods/fukui/06_oroshi_soba.webp',
    radius: 42,
    mass: 3.5,
    score: 64,
    description: '辛味大根のおろしを、そばごと。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/oroshi_soba_fukui.html',
    icon: { label: 'おろし', shape: 'noodle-bowl', baseColor: '#8a9a7c', accentColor: '#e8e0cc' },
    reward: {
      unlockScore: 4500,
      area: '嶺北',
      headline: '1601年、府中城主・本多富正がそば職人を連れて赴任し、大根おろしをのせた細い麺を広めたと伝わる。',
      tip: '昭和天皇が福井を訪れたときに召し上がり「越前のそばは大変おいしかった」と述べたことで、全国に知られるようになった。越前辛味大根を使うのが一般的。',
      links: searchLinks('福井県', 'おろしそば'),
    },
  },
  {
    id: 'hamayaki-saba',
    level: 7,
    name: '浜焼き鯖',
    shortName: '浜焼き鯖',
    image: '/assets/foods/fukui/07_hamayaki_saba.webp',
    radius: 46,
    mass: 4.4,
    score: 128,
    description: 'とれたての鯖1尾を竹か茅の串で頭から尾にかけて刺し、炭火で25分ほどかけてじっくり焼く。若狭地域や大野市に伝わる。生姜醤油や大根おろしをかけて食べるのが一般的。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/traditional-foods/menu/hamayaki_saba.html',
    icon: { label: '焼き鯖', shape: 'fish', baseColor: '#b07a3c', accentColor: '#3f6b78' },
    reward: {
      unlockScore: 6500,
      area: '小浜市ほか若狭地域／大野市',
      headline: '鯖を丸ごと串に刺して炭火で焼く',
      tip: '大野市では、殿様が田植えで疲れた農民に丸焼き鯖を配ったと伝わり、半夏生（7月2日ごろ）に食べる習わしが残る。',
      links: searchLinks('小浜市', '浜焼き鯖'),
      official: [{ label: '若狭おばま観光協会「浜焼き鯖」', url: 'https://wakasa-obama.jp/ingredients/saba/' }, { label: '若狭おばま観光協会「小浜に来たら『浜焼き鯖』を食すべし」', url: 'https://wakasa-obama.jp/special/hamayaki-saba-obama/' }],
    },
  },
  {
    id: 'sauce-katsudon',
    level: 8,
    name: 'ソースカツ丼',
    shortName: 'ソースカツ丼',
    image: '/assets/foods/fukui/08_sauce_katsudon.webp',
    radius: 51,
    mass: 5.4,
    score: 256,
    description: '卵でとじない。キャベツも敷かない。',
    source: 'https://fuku-iro.jp/feature/katsudon',
    icon: { label: 'カツ丼', shape: 'rice-bowl', baseColor: '#8a5a32', accentColor: '#f3e6c6' },
    reward: {
      unlockScore: 9000,
      area: '福井市',
      headline: '卵でとじず、キャベツも敷かず、ご飯の上にソースをくぐらせたカツだけを載せる。',
      tip: '福井市公式観光サイトによると、ドイツ留学から明治45年に帰国した高畠増太郎が、大正2年に東京の料理発表会で初めて披露したとされる。',
      links: searchLinks('福井市', 'ソースカツ丼'),
    },
  },
  {
    id: 'volga-rice',
    level: 9,
    name: 'ボルガライス',
    shortName: 'ボルガライス',
    image: '/assets/foods/fukui/09_volga_rice.webp',
    radius: 57,
    mass: 6.6,
    score: 512,
    description: 'オムライスの上に、カツ。',
    source: 'https://www.city.echizen.lg.jp/office/060/050/volgarice.html',
    icon: { label: 'ボルガ', shape: 'curry-plate', baseColor: '#e2a53c', accentColor: '#b5563f' },
    reward: {
      unlockScore: 12000,
      area: '越前市',
      headline: '越前市のご当地グルメ。オムライスの上にトンカツをのせる。',
      tip: '越前市の公式サイトは「名前の由来や発祥のお店が分かっておらず、正式なレシピもありません」と書いている。店によってメンチカツを使ったり、ソースが違ったりする。',
      links: searchLinks('越前市', 'ボルガライス'),
    },
  },
  {
    id: 'wakasa-guji',
    level: 10,
    name: '若狭ぐじ（甘鯛）の料理',
    shortName: '若狭ぐじ',
    image: '/assets/foods/fukui/10_wakasa_guji.webp',
    radius: 63,
    mass: 8,
    score: 1024,
    description: '若狭湾で延縄や釣りでとれたアカアマダイのうち、500g以上で鮮度がよく姿形の美しいものが「若狭ぐじ」と呼ばれる。うろこを取らずに焼く「若狭焼き」では、焼き色を見ながら若狭地（たれ）を重ねて焼く。',
    source:
      'https://www.pref.fukui.lg.jp/doc/suisan/ryutsu/wakasaguji.html',
    icon: { label: 'ぐじ', shape: 'fish', baseColor: '#e08a7a', accentColor: '#e8c45a' },
    reward: {
      unlockScore: 16000,
      area: '若狭地方（若狭湾沿岸）',
      headline: 'うろこごと焼く朱色の魚',
      tip: '古くから京料理の食材として珍重され、京の都に運ばれた。焼き物のほか、昆布締め・刺身・揚げ物・酒蒸し・椀物にもされる。',
      links: searchLinks('小浜市', '若狭ぐじ'),
      official: [{ label: '福井県観光連盟 ふくいドットコム「御食国を代表する『若狭ぐじ』」', url: 'https://www.fuku-e.com/feature/wakasa-guji' }],
    },
  },
  {
    id: 'wakasa-fugu',
    level: 11,
    name: '若狭ふぐ',
    shortName: '若狭ふぐ',
    image: '/assets/foods/fukui/11_wakasa_fugu.webp',
    radius: 70,
    mass: 9.6,
    score: 2048,
    description: '日本でいちばん北の海で育つ、とらふぐ。',
    source: 'https://info.pref.fukui.lg.jp/suisan/fuguyado/column/column.html',
    icon: { label: 'ふぐ', shape: 'sashimi-platter', baseColor: '#f0e6d8', accentColor: '#5b7f9c' },
    reward: {
      unlockScore: 22000,
      area: '嶺南',
      headline: '敦賀市から高浜町にかけての海で養殖されるトラフグ。県は「日本最北の海上でのトラフグ養殖生産地」と記している。',
      tip: '昭和60年ごろ、大阪の黒門市場で「若狭ふぐ」と呼ばれたのが名前のもと。冬のイメージが強いが、県は夏に食べることも勧めている。',
      links: searchLinks('福井県', '若狭ふぐ'),
    },
  },
  {
    id: 'echizen-gani',
    level: 12,
    name: '越前がに',
    shortName: '越前がに',
    image: '/assets/foods/fukui/12_echizen_gani.webp',
    radius: 78,
    mass: 11.4,
    score: 4096,
    description: '黄色いタグが付いた、冬の王様。',
    source: 'https://www.pref.fukui.lg.jp/doc/suisan/ryutsu/echizengani_1.html',
    icon: { label: '越前がに', shape: 'feast', baseColor: '#c1392b', accentColor: '#f4d9a8' },
    reward: {
      unlockScore: 30000,
      area: '福井県',
      headline: '福井県の港で水揚げされたズワイガニ。水揚げした漁港が刻まれた黄色いタグが目印。',
      tip: '解禁日は毎年11月6日。雄は3月20日まで、雌の「せいこがに」は12月31日まで。平成30年9月27日に地理的表示（GI）へ登録され、タグにGIマークが加わった。福井県の「県の魚」でもある。',
      links: searchLinks('福井県', '越前がに'),
    },
  },
];

/** 得点は 2 ^ level を基準にしている（GAME_TUNING.score で方式変更可能）。 */
export const fukuiFoodStage: StageDefinition = {
  regionId: 'fukui',
  regionName: '福井県',
  mode: 'food',
  title: '福井めしマージ',
  theme: {
    primaryColor: '#2f5f8a',
    secondaryColor: '#d9a441',
    accentColor: '#f2d16b',
    backgroundColor: '#15191f',
  },
  /** 背景ツアー「福井めぐり」。軸は季節。5月から翌4月へ一周する。 */
  scenery: {
    tour: fukuiSceneryTour,
  },
  items: attachHulls(FOOD_ITEMS, FUKUI_FOOD_HULLS),
};
